import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlay as Play, FiChevronRight as ChevronRight, FiClock as Clock, FiUser as User, FiActivity as Activity, FiAlertCircle as AlertCircle, FiTrash2 as TrashIcon, FiSquare as Square, FiCheckSquare as CheckSquare, FiSearch as SearchIcon, FiFilter as FilterIcon, FiRefreshCw as RefreshIcon, FiFileText as FileText } from 'react-icons/fi';
import { getAgentRuns, bulkDeleteAgentRuns, bulkAssignRunProject, bulkAssignRunWorkload } from '../../../../services/aiOperationsApi';
import { markGenAIOpsStepComplete } from '../../../../hooks/useOnboardingStatus';
import { useProject } from '../../../../contexts/ProjectContext';
import { useFilter } from '../../../../contexts/FilterContext';
import ProjectAssignmentSelect from '../../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../../components/WorkloadAssignmentSelect';

interface AgentRunListItem {
  id: string;
  agentName?: string;
  agent_name?: string;
  status?: string;
  outcome?: string;
  task?: string;
  startedAt?: string;
  started_at?: string;
  start_time?: string;
  end_time?: string;
  endTime?: string;
  ended_at?: string;
  duration?: number;
  userId?: string;
  user_id?: string;
  sessionId?: string;
  session_id?: string;
  inputTokens?: number;
  input_tokens?: number;
  outputTokens?: number;
  output_tokens?: number;
  steps_count?: number;
  stepsCount?: number;
  tools_used?: number;
  toolsUsed?: number;
  cost?: number;
  error_message?: string;
  errorMessage?: string;
  project_id?: string;
  workload_id?: string;
}

interface RunFilters {
  agentName: string;
  status: string;
  userId: string;
  sessionId: string;
}

const AgentRunsPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedProjectId, projects } = useProject();
  const { filterType, selectedId: filterId } = useFilter();
  const [runs, setRuns] = useState<AgentRunListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<RunFilters>({
    agentName: '',
    status: '',
    userId: '',
    sessionId: ''
  });

  // Mark onboarding step as complete when visiting this page
  useEffect(() => {
    markGenAIOpsStepComplete('agent_run');
  }, []);

  useEffect(() => {
    loadRuns();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, selectedProjectId, filterType, filterId]);

  const loadRuns = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        limit: 50,
        ...Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== '')
        )
      };

      const data = await getAgentRuns(params);
      setRuns(data.data || data.runs || []);
      setTotalPages(data.meta?.totalPages || 1);
    } catch (err: unknown) {
      console.error('Error loading agent runs:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === runs.length ? new Set() : new Set(runs.map((r) => r.id))
    );
  }, [runs]);

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!globalThis.confirm(`Delete ${count} run${count > 1 ? 's' : ''}? They will be excluded from the list and metrics.`)) return;
    try {
      await bulkDeleteAgentRuns(Array.from(selectedIds));
      setSelectedIds(new Set());
      loadRuns();
    } catch (deleteErr: unknown) {
      console.error('Error deleting runs:', deleteErr);
    }
  };

  const handleRunClick = (runId: string) => {
    navigate(`/ai-operations/runs/${runId}`);
  };

  const formatTimestamp = (timestamp: string | null | undefined): string => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (seconds: number | null | undefined): string => {
    if (seconds == null) return 'N/A';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(0);
    return `${minutes}m ${secs}s`;
  };

  const getOutcomeColor = (outcome: string | undefined): string => {
    switch (outcome?.toLowerCase()) {
      case 'success':
        return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
      case 'error':
      case 'failure':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'timeout':
        return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300';
      case 'warning':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'partial_success':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      case 'cancelled':
        return 'bg-gray-400 dark:bg-gray-600 text-gray-800 dark:text-gray-300';
      case 'retrying':
        return 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status: string | undefined): React.ReactElement => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return <Activity className="w-4 h-4 text-green-600" />;
      case 'failed':
      case 'error':
      case 'failure':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'running':
        return <Play className="w-4 h-4 text-yellow-600 animate-pulse" />;
      case 'timeout':
        return <AlertCircle className="w-4 h-4 text-orange-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
          Agent Runs
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
          Monitor and analyze agent execution runs across your applications
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <div className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <button onClick={toggleSelectAll} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
              {selectedIds.size === runs.length && runs.length > 0
                ? <CheckSquare className="w-5 h-5 text-blue-600" />
                : <Square className="w-5 h-5 text-gray-400" />}
            </button>
            <div className="flex items-center gap-2">
              <FilterIcon className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by agent name..."
                  value={filters.agentName}
                  onChange={(e) => setFilters({ ...filters, agentName: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Statuses</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
            <button
              type="button"
              onClick={() => loadRuns()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <RefreshIcon className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">{selectedIds.size} selected</span>
            <button type="button" onClick={() => setSelectedIds(new Set())} className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">Clear selection</button>
          </div>
          <div className="flex items-center gap-2">
            <ProjectAssignmentSelect
              bulk
              currentProjectId={null}
              onAssign={async (projectId) => {
                await bulkAssignRunProject(Array.from(selectedIds), projectId);
                setRuns(prev => prev.map(r => selectedIds.has(r.id) ? { ...r, project_id: projectId ?? undefined } : r));
                setSelectedIds(new Set());
              }}
              size="sm"
            />
            <WorkloadAssignmentSelect
              bulk
              onAssign={async (workloadId) => {
                const ids = Array.from(selectedIds);
                await bulkAssignRunWorkload(ids, workloadId);
                setSelectedIds(new Set());
                loadRuns();
              }}
              size="sm"
            />
            <button type="button" onClick={handleBulkDelete} className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition-colors">
              <TrashIcon className="w-4 h-4" />Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800
                      rounded-lg p-4 transition-colors">
          <p className="text-red-800 dark:text-red-200">
            Error loading agent runs: {error}
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Runs List */}
      {!loading && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden transition-colors">
          {runs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">
                No agent runs found. Try adjusting your filters.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {runs.map((run) => (
                <div
                  key={run.id}
                  onClick={() => handleRunClick(run.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRunClick(run.id); }} role="button" tabIndex={0}
                  className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer
                           transition-colors group"
                >
                  <div className="flex items-start gap-4">
                    <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }} role="presentation" className="pt-1 flex-shrink-0">
                      <button onClick={() => toggleSelect(run.id)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                        {selectedIds.has(run.id) ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5 text-gray-400" />}
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        {getStatusIcon(run.outcome || run.status)}
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {run.agentName || run.agent_name || 'Unknown Agent'}
                        </h3>
                        {run.outcome && (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getOutcomeColor(run.outcome)}`}>
                            {run.outcome}
                          </span>
                        )}
                        {(() => {
                          const pName = run.project_id ? projects.find(p => p.id === run.project_id)?.name : null;
                          return pName ? (
                            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded">
                              {pName}
                            </span>
                          ) : null;
                        })()}
                        {(run as any).workload_name && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 rounded">
                            {(run as any).workload_name}
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {run.task || 'No task description'}
                      </p>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          Started: {formatTimestamp(run.startedAt || run.started_at || run.start_time)}
                        </div>
                        {run.duration && (
                          <div className="flex items-center">
                            Duration: {formatDuration(run.duration)}
                          </div>
                        )}
                        {(run.userId || run.user_id) && (
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-1" />
                            {run.userId || run.user_id}
                          </div>
                        )}
                        {(run.sessionId || run.session_id) && (
                          <div className="flex items-center">
                            <Activity className="w-4 h-4 mr-1" />
                            Session: {(run.sessionId || run.session_id)?.substring(0, 20)}...
                          </div>
                        )}
                        {(run.inputTokens || run.input_tokens) && (
                          <div className="flex items-center">
                            Tokens: {(run.inputTokens || run.input_tokens)?.toLocaleString()}↓ /
                            {(run.outputTokens || run.output_tokens || 0).toLocaleString()}↑
                          </div>
                        )}
                        {(run.end_time || run.endTime || run.ended_at) && run.status?.toLowerCase() === 'completed' && (
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            Ended: {formatTimestamp(run.end_time || run.endTime || run.ended_at)}
                          </div>
                        )}
                        {(run.steps_count ?? run.stepsCount) != null && (
                          <div className="flex items-center">
                            Steps: {run.steps_count || run.stepsCount}
                          </div>
                        )}
                        {(run.tools_used ?? run.toolsUsed) != null && (
                          <div className="flex items-center">
                            Tools: {run.tools_used || run.toolsUsed}
                          </div>
                        )}
                        {run.cost != null && (
                          <div className="flex items-center">
                            Cost: ${Number(run.cost).toFixed(4)}
                          </div>
                        )}
                      </div>

                      {(run.error_message || run.errorMessage) && (
                        <div className="mt-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded">
                          Error: {run.error_message || run.errorMessage}
                        </div>
                      )}

                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-sm text-gray-500 dark:text-gray-500 font-mono">
                          ID: {run.id}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/ai-operations/traces/${run.id}`);
                            }}
                            className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center gap-1 px-2 py-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                            title="View Trace Details"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            Trace
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/ai-operations/runs/${run.id}?tab=flamegraph`);
                            }}
                            className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                            title="View Flamegraph"
                          >
                            <Activity className="w-3.5 h-3.5" />
                            Flamegraph
                          </button>
                        </div>
                      </div>
                    </div>

                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600
                                           dark:group-hover:text-gray-300 transition-colors flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="mt-6 flex justify-center items-center space-x-4">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                     text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                     disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="text-gray-600 dark:text-gray-400">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                     text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                     disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AgentRunsPage;
